export interface ReleaseNote {
  version: string;
  notes: string;
  updated_at?: number;
  updated_by?: string;
}

export interface ReleaseNoteEntry extends ReleaseNote {
  key: string;
}

export type ReleaseNotesByVersion = Record<string, ReleaseNote | null>;

export const MAX_RENDERED_RELEASE_NOTES = 20;
export const MAX_LOADED_RELEASE_NOTES = MAX_RENDERED_RELEASE_NOTES;

export const getReleaseNotesHoldingMessage = (version: string) =>
  `Release notes for v${version} are coming soon.`;

const hasInvalidFirebaseKeyCharacter = (key: string) =>
  ['.', '#', '$', '/', '[', ']'].some((character) => key.includes(character)) ||
  Array.from(key).some((character) => {
    const codePoint = character.codePointAt(0) || 0;
    return codePoint <= 31 || codePoint === 127;
  });

const isReleaseNote = (value: unknown): value is ReleaseNote => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const releaseNote = value as Partial<ReleaseNote>;
  return (
    typeof releaseNote.version === 'string' &&
    typeof releaseNote.notes === 'string'
  );
};

export const getReleaseNoteEntries = (
  releaseNotes: ReleaseNotesByVersion | undefined,
  currentKey: string,
  currentVersion: string,
  currentReleaseNote?: ReleaseNote | null
): ReleaseNoteEntry[] => {
  const entries = Object.entries(releaseNotes || {}).reduce<ReleaseNoteEntry[]>(
    (result, [key, value]) => {
      if (isReleaseNote(value)) {
        result.push({ key, ...value });
      }
      return result;
    },
    []
  );

  const currentRelease = isReleaseNote(currentReleaseNote)
    ? { key: currentKey, ...currentReleaseNote }
    : entries.find((entry) => entry.key === currentKey) || {
        key: currentKey,
        version: currentVersion,
        notes: getReleaseNotesHoldingMessage(currentVersion),
      };

  const previousReleases = entries
    .filter((entry) => entry.key !== currentKey)
    .sort((a, b) =>
      b.version.localeCompare(a.version, undefined, { numeric: true })
    );

  return [
    currentRelease,
    ...previousReleases.slice(0, MAX_RENDERED_RELEASE_NOTES - 1),
  ];
};

export const findReleaseNoteEntry = (
  entries: ReleaseNoteEntry[],
  key: string
) => entries.find((entry) => entry.key === key);

export const getReleaseNotePath = ({ key }: ReleaseNoteEntry) => {
  if (!key || hasInvalidFirebaseKeyCharacter(key)) {
    throw new Error(
      'Cannot create a Firebase path for an invalid release key.'
    );
  }

  return `release_notes/${key}`;
};
